







import { watchEffect, defineComponent, reactive, ref, onMounted } from '@vue/composition-api'

interface ChartData {
  start_value: string
  end_value: string
  interest: string
  tax: string
  payment: string
  interest1: string
  tax1: string
  payment1: string
  interest2: string
  tax2: string
  payment2: string
}

export default defineComponent({
  props: {
    data : Array as () => ChartData[],
    prev : Array as () => ChartData[],
    faded_colors: { type: Boolean, default: false }
  },
  setup(props) {
    let min_x = 0.0
    let max_x = 1.0
    let min_y = 0.0
    let max_y = 1.0
    let is_busy = false

    const mycanvas = ref<HTMLCanvasElement | null>(null)

    const Chart = {
      m_left: 1,
      m_right: 1,
      m_bot: 1,
      m_top: 1
    }

    const Colors = {
      data_bg: getComputedStyle(document.documentElement).getPropertyValue('--white'),
      data_fg: getComputedStyle(document.documentElement).getPropertyValue('--primary-dark'),
      border: getComputedStyle(document.documentElement).getPropertyValue('--primary'),
      blue: getComputedStyle(document.documentElement).getPropertyValue('--blue'),
      red: getComputedStyle(document.documentElement).getPropertyValue('--red'),
      cyan: getComputedStyle(document.documentElement).getPropertyValue('--cyan'),
      blue2: getComputedStyle(document.documentElement).getPropertyValue('--blue2'),
      red2: getComputedStyle(document.documentElement).getPropertyValue('--red2'),
      cyan2: getComputedStyle(document.documentElement).getPropertyValue('--cyan2'),
    }

    function calc_x(xval : number) {
      if (!mycanvas.value) { return 0.0 }
      const pct_x = ((xval - min_x) / (max_x - min_x))
      return Chart.m_left + (mycanvas.value.width - (Chart.m_left + Chart.m_right)) * pct_x
    }

    function calc_y(yval : number) {
      if (!mycanvas.value) { return 0.0 }
      const pct_y = (1 - (yval - min_y) / (max_y - min_y))
      return Chart.m_top + (mycanvas.value.height - (Chart.m_bot + Chart.m_top)) * pct_y
    }

    function calc_pct_x(xval : number) {
      if (!mycanvas.value) { return 0.0 }
      return Chart.m_left + (mycanvas.value.width - (Chart.m_left + Chart.m_right)) * xval
    }

    function calc_pct_y(yval : number) {
      if (!mycanvas.value) { return 0.0 }
      return Chart.m_top + (mycanvas.value.height - (Chart.m_bot + Chart.m_top)) * (1 - yval)
    }

    function calc_pct_width(xval : number) {
      if (!mycanvas.value) { return 0.0 }
      return (mycanvas.value.width - (Chart.m_left + Chart.m_right)) * xval
    }

    function calc_pct_height(yval : number) {
      if (!mycanvas.value) { return 0.0 }
      return (mycanvas.value.height - (Chart.m_bot + Chart.m_top)) * (1 - yval)
    }

    function border_background(context : CanvasRenderingContext2D) {
      /* eslint-disable no-param-reassign */
      context.globalAlpha = 1.0
      context.strokeStyle = Colors.data_fg
      context.fillStyle = Colors.border
      context.fillRect(0, 0, context.canvas.width, context.canvas.height)
      context.fillStyle = Colors.data_bg

      context.fillRect(
        calc_pct_x(0),
        calc_pct_y(1),
        (calc_pct_x(1) - calc_pct_x(0)),
        (calc_pct_y(0) - calc_pct_y(1)),
      )
      /* eslint-enable no-param-reassign */
    }

    function border_background2(context : CanvasRenderingContext2D) {
      /* eslint-disable no-param-reassign */
      context.globalAlpha = 1.0
      context.strokeStyle = Colors.data_fg
      context.fillStyle = Colors.border
      context.fillRect(0, 0, context.canvas.width, context.canvas.height)
      context.fillStyle = Colors.data_bg

      context.fillRect(
        calc_pct_x(0),
        calc_pct_y(1),
        (calc_pct_x(1) - calc_pct_x(0)),
        (calc_pct_y(0) - calc_pct_y(1)),
      )
      /* eslint-enable no-param-reassign */
    }

    function render_prev(context: CanvasRenderingContext2D, line_index : number, total_lines : number, line : ChartData) {
      const right_pad = 0.0 // 2.0
      const bar_pct = 0.0 // 0.3
      const left = calc_pct_x((line_index) / (total_lines + bar_pct + right_pad))
      const step_width = calc_pct_width(1.0 / (total_lines + bar_pct + right_pad))
      const right = calc_pct_x((line_index + 1 ) / (total_lines + bar_pct + right_pad))

      const y_interest1 = calc_y(parseFloat(line.interest1))
      const y_interest2 = calc_y(parseFloat(line.interest2))
      const y_payment1 = calc_y(parseFloat(line.payment1))
      const y_payment2 = calc_y(parseFloat(line.payment2))
      const y_tax1 = calc_y(-parseFloat(line.tax1))
      const y_tax2 = calc_y(-parseFloat(line.tax2))
      const y0 = calc_y(0.0)

      context.fillStyle = Colors.cyan2
      context.strokeStyle = Colors.cyan2
      context.beginPath()
      context.moveTo(left, y_payment1)
      context.lineTo(right, y_payment2)
      context.lineTo(right, y0)
      context.lineTo(left, y0)
      context.stroke()
      context.fill()

      context.fillStyle = Colors.blue2
      context.strokeStyle = Colors.blue2
      context.beginPath()
      context.moveTo(left, y_interest1)
      context.lineTo(right, y_interest2)
      context.lineTo(right, y_payment2)
      context.lineTo(left, y_payment1)
      context.stroke()
      context.fill()

      context.fillStyle = Colors.red2
      context.strokeStyle = Colors.red2
      context.beginPath()
      context.moveTo(left, y_tax1)
      context.lineTo(right, y_tax2)
      context.lineTo(right, y0)
      context.lineTo(left, y0)
      context.stroke()
      context.fill()

    }

    function render_line2(context: CanvasRenderingContext2D, line_index : number, total_lines : number, line : ChartData) {
      const right_pad = 0.0 // 2.0
      const bar_pct = 0.0 // 0.3
      const left = calc_pct_x((line_index) / (total_lines + bar_pct + right_pad))
      const step_width = calc_pct_width(1.0 / (total_lines + bar_pct + right_pad))
      const right = calc_pct_x((line_index + 1 ) / (total_lines + bar_pct + right_pad))
      const y1 = calc_y(parseFloat(line.start_value))
      const y2 = calc_y(parseFloat(line.end_value))
      const y0 = calc_y(0.0)

      const y_interest1 = calc_y(parseFloat(line.interest1))
      const y_interest2 = calc_y(parseFloat(line.interest2))
      const y_tax1 = calc_y(-parseFloat(line.tax1))
      const y_tax2 = calc_y(-parseFloat(line.tax2))
      const y_payment1 = calc_y(parseFloat(line.payment1))
      const y_payment2 = calc_y(parseFloat(line.payment2))
      const y_sum1 = calc_y(parseFloat(line.interest1) - parseFloat(line.tax1))
      const y_sum2 = calc_y(parseFloat(line.interest2) - parseFloat(line.tax2))

      context.fillStyle = props.faded_colors ? Colors.cyan2 : Colors.cyan
      context.strokeStyle = props.faded_colors ? Colors.cyan2 : Colors.cyan
      context.beginPath()
      context.moveTo(left, y_payment1)
      context.lineTo(right, y_payment2)
      context.lineTo(right, y0)
      context.lineTo(left, y0)
      context.stroke()
      context.fill()

      context.fillStyle = props.faded_colors ? Colors.blue2 : Colors.blue
      context.strokeStyle = props.faded_colors ? Colors.blue2 : Colors.blue
      context.beginPath()
      context.moveTo(left, y_interest1)
      context.lineTo(right, y_interest2)
      context.lineTo(right, y_payment2)
      context.lineTo(left, y_payment1)
      context.stroke()
      context.fill()

      context.fillStyle = props.faded_colors ? Colors.red2 : Colors.red
      context.strokeStyle = props.faded_colors ? Colors.red2 : Colors.red
      context.beginPath()
      context.moveTo(left, y_tax1)
      context.lineTo(right, y_tax2)
      context.lineTo(right, y0)
      context.lineTo(left, y0)
      context.stroke()
      context.fill()

      context.lineWidth = 1
      context.fillStyle = '#ffffff'
      context.strokeStyle = '#ffffff'
      context.beginPath()
      context.moveTo(left, y_sum1)
      context.lineTo(right, y_sum2)
      context.stroke()


      const end2 = parseFloat(line.interest2) - parseFloat(line.tax2)

      // console.log({end: line.end_value, payment2: line.payment2, interest2: line.interest2, tax2: line.tax2, end2})
    }

    function render(data: ChartData[], prev: ChartData[]) {
      // console.log('render')
      if (!mycanvas.value) { return }
      const context = mycanvas.value.getContext('2d')
      if (!context) { return }
      border_background(context)
      // console.log('border')

      if (data.length === 0) { return }
      const max_y1 = parseFloat(data[data.length - 1].interest2)
      const max_y2 = prev.length > 0 ? parseFloat(prev[prev.length - 1].interest2) : 0
      max_y = Math.max(max_y1, max_y2)
      // console.log({max_y})
      min_y = -0.02 * max_y - parseFloat(data[data.length -1].tax2)

      context.strokeStyle = "#606060"
      context.beginPath()
      context.moveTo(calc_pct_x(0), calc_y(0))
      context.lineTo(calc_pct_x(1), calc_y(0))
      context.stroke()

      data.forEach((line : ChartData, index) => {
        render_line2(context, index, data.length, line)
      })
    }

    watchEffect(() => {
      if (props.prev !== undefined) {
        // console.log('prev')
        // console.log(props.prev.map(item => item.end_value))
      }
      if (props.data !== undefined) {
        // console.log('data')
        // console.log(props.data.map(item => item.end_value))
      }
      if (props.data && props.prev && !is_busy) {
        is_busy = true
        render(props.data, props.prev)
        is_busy = false
      }
    })

    return {
      mycanvas,
    }
  },
})

